import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase';
import { getActiveEventId, getEventById, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import {
  ensureMainClueEventConfigMigrated,
  subscribeMainClueEventConfig,
  saveSetAssignments,
  saveSponsorQuestionIds,
  getMainClueEventConfig,
} from '../utils/mainClueEventConfig';
import {
  listMainClueQuestions,
  createMainClueQuestion,
  updateMainClueQuestion,
  deleteMainClueQuestion,
  listQuestionUsage,
  syncUsageForSponsorQuestions,
  rebuildQuestionUsedBySponsors,
} from '../utils/mainClueQuestionBank';
import { buildSponsorRunSheetsHtml, getSponsorRunPrintCss } from '../utils/mainClueSponsorRunPrint';
import { openPrintWindow } from '../utils/printUtils';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';
import './MainClueSponsorRunAdmin.css';
import './admin/FmModal.css';

const TABS = {
  library: 'library',
  event: 'event',
  print: 'print',
};

function filterInteractiveForMainClue(list) {
  return (list || []).filter(
    (s) =>
      s &&
      !s.hidden &&
      (s.type || 'none') === 'interactive' &&
      s.showOnDashboard !== false
  );
}

function tagsToString(tags) {
  if (!Array.isArray(tags)) return '';
  return tags.join(', ');
}

function parseTags(s) {
  return (s || '')
    .split(/[,;\n]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Lowercased haystack of sponsor usage (names, org ids, years, sets) for search. */
function mainClueQuestionUsageHaystack(q) {
  const parts = [];
  (q.usedBySponsors || []).forEach((s) => {
    if (s.name) parts.push(s.name);
    if (s.organizationId) parts.push(s.organizationId);
  });
  (q.sponsorUsageHistory || []).forEach((h) => {
    if (h.name) parts.push(h.name);
    if (h.organizationId) parts.push(h.organizationId);
    (h.uses || []).forEach((u) => {
      if (u.eventYear != null) parts.push(String(u.eventYear));
      if (u.setId) parts.push(u.setId, `set ${u.setId}`);
      if (u.eventId) parts.push(u.eventId);
    });
  });
  return parts.join(' ').toLowerCase();
}

function formatMainCluePriorUseLine(q) {
  const hist = q.sponsorUsageHistory;
  if (Array.isArray(hist) && hist.length) {
    return hist
      .map((h) => {
        const bits = (h.uses || []).map((u) => {
          const y = u.eventYear != null ? String(u.eventYear) : '?';
          return u.setId ? `${y} · ${u.setId}` : y;
        });
        return `${h.name} (${bits.join(', ')})`;
      })
      .join(' · ');
  }
  const names = (q.usedBySponsors || []).map((s) => s.name).filter(Boolean);
  if (names.length) {
    return `Prior sponsors: ${names.join(', ')} (years appear after Library → History refresh)`;
  }
  return 'No prior recorded usage.';
}

function formatMainCluePriorUseShort(q) {
  const hist = q.sponsorUsageHistory;
  if (Array.isArray(hist) && hist.length) {
    return hist.map((h) => h.name).filter(Boolean).join(', ') || '—';
  }
  return (q.usedBySponsors || []).map((s) => s.name).join(', ') || '—';
}

function mainClueQuestionUsedByPickingSponsor(q, sponsor) {
  if (!sponsor) return false;
  const oid = (sponsor.organizationId || '').toString().trim();
  const sid = (sponsor.id || '').toString().trim();
  const hist = q.sponsorUsageHistory;
  if (Array.isArray(hist)) {
    for (const h of hist) {
      if (oid && h.organizationId && h.organizationId === oid) return true;
      if (!oid && sid && h.sponsorParticipationId === sid) return true;
    }
  }
  if (oid && (q.usedBySponsors || []).some((s) => s.organizationId === oid)) return true;
  return false;
}

export default function MainClueSponsorRunAdmin() {
  const [searchParams, setSearchParams] = useSearchParams();
  const eventParam = (searchParams.get('event') || '').trim();

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [eventMeta, setEventMeta] = useState(null);
  const [tab, setTab] = useState(TABS.event);

  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');

  const [sponsors, setSponsors] = useState([]);
  const [sponsorsLoading, setSponsorsLoading] = useState(false);
  const [firestoreSetMap, setFirestoreSetMap] = useState({});
  const [sponsorQuestionIds, setSponsorQuestionIds] = useState({});
  const firestoreSetMapRef = useRef({});
  useEffect(() => {
    firestoreSetMapRef.current = firestoreSetMap;
  }, [firestoreSetMap]);

  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const [qModal, setQModal] = useState(null);
  const [usageModal, setUsageModal] = useState(null);
  const [usageRows, setUsageRows] = useState([]);
  const [copyModal, setCopyModal] = useState(null);
  const [copySponsorId, setCopySponsorId] = useState('');
  const [pickSponsorModal, setPickSponsorModal] = useState(null);
  const [newQForSponsor, setNewQForSponsor] = useState(null);

  const [printSetScope, setPrintSetScope] = useState('all');

  useEffect(() => {
    if (!copyModal) return;
    setCopySponsorId((prev) => {
      if (prev && sponsors.some((s) => s.id === prev)) return prev;
      return sponsors[0]?.id || '';
    });
  }, [copyModal, sponsors]);

  useModalEscapeKey(!!qModal, () => setQModal(null));
  useModalEscapeKey(!!usageModal, () => setUsageModal(null));
  useModalEscapeKey(!!copyModal, () => setCopyModal(null));
  useModalEscapeKey(!!pickSponsorModal, () => setPickSponsorModal(null));
  useModalEscapeKey(!!newQForSponsor, () => setNewQForSponsor(null));

  const refreshQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const list = await listMainClueQuestions();
      setQuestions(list);
    } catch (e) {
      setError(e.message || 'Failed to load question bank');
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        if (cancelled) return;
        setEvents(list);
        setSelectedEventId(
          resolveAdminDefaultEventId({
            eventIdParam: eventParam,
            activeEventId: activeId,
            events: list,
          })
        );
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load events');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventParam]);

  useEffect(() => {
    if (!selectedEventId) {
      setEventMeta(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const ev = await getEventById(selectedEventId);
      if (!cancelled) setEventMeta(ev);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId]);

  useEffect(() => {
    refreshQuestions();
  }, [refreshQuestions]);

  useEffect(() => {
    if (!selectedEventId) {
      setSponsors([]);
      setFirestoreSetMap({});
      setSponsorQuestionIds({});
      return;
    }
    let cancelled = false;
    setSponsorsLoading(true);
    (async () => {
      try {
        const merged = await fetchSponsorsMergedForEvent(selectedEventId);
        if (cancelled) return;
        const list = filterInteractiveForMainClue(merged).sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
        );
        setSponsors(list);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load sponsors');
      } finally {
        if (!cancelled) setSponsorsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) {
      setFirestoreSetMap({});
      setSponsorQuestionIds({});
      return;
    }
    let unsub = () => {};
    let cancelled = false;
    (async () => {
      try {
        await ensureMainClueEventConfigMigrated(selectedEventId);
        if (cancelled) return;
        unsub = subscribeMainClueEventConfig(selectedEventId, (data) => {
          setFirestoreSetMap(data.setAssignments || {});
          setSponsorQuestionIds(data.sponsorQuestionIds || {});
        });
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [selectedEventId]);

  const questionsById = useMemo(() => {
    const m = {};
    questions.forEach((q) => {
      m[q.id] = q;
    });
    return m;
  }, [questions]);

  const editionYear = eventMeta?.editionYear != null ? Number(eventMeta.editionYear) : null;

  const filteredLibrary = useMemo(() => {
    const t = librarySearch.trim().toLowerCase();
    if (!t) return questions;
    return questions.filter((q) => {
      const prompt = (q.prompt || '').toLowerCase();
      const tags = (q.tags || []).join(' ').toLowerCase();
      const usageHay = mainClueQuestionUsageHaystack(q);
      return prompt.includes(t) || tags.includes(t) || usageHay.includes(t);
    });
  }, [questions, librarySearch]);

  const persistSponsorQuestions = async (sponsorId, ids, sponsorRow) => {
    if (!selectedEventId) return;
    const cfg = await getMainClueEventConfig(selectedEventId);
    const next = { ...cfg.sponsorQuestionIds, [sponsorId]: ids };
    await saveSponsorQuestionIds(selectedEventId, next);
    const setId = firestoreSetMapRef.current[sponsorId] || null;
    await syncUsageForSponsorQuestions(
      selectedEventId,
      sponsorRow,
      setId,
      ids,
      editionYear
    );
  };

  const handleSetChange = async (sponsorId, nextSet) => {
    if (!selectedEventId) return;
    const cleaned = { ...firestoreSetMapRef.current };
    if (nextSet === 'A' || nextSet === 'B' || nextSet === 'C') cleaned[sponsorId] = nextSet;
    else delete cleaned[sponsorId];
    try {
      await saveSetAssignments(selectedEventId, cleaned);
      const sponsor = sponsors.find((x) => x.id === sponsorId);
      const qids = sponsorQuestionIds[sponsorId] || [];
      const setId = nextSet === 'A' || nextSet === 'B' || nextSet === 'C' ? nextSet : null;
      if (sponsor && qids.length) {
        await syncUsageForSponsorQuestions(selectedEventId, sponsor, setId, qids, editionYear);
      }
    } catch (e) {
      setError(e.message || 'Failed to save set');
    }
  };

  const handleRemoveQuestion = async (sponsor, qIndex) => {
    const ids = [...(sponsorQuestionIds[sponsor.id] || [])];
    ids.splice(qIndex, 1);
    try {
      await persistSponsorQuestions(sponsor.id, ids, sponsor);
      setOkMsg('Questions updated');
      setTimeout(() => setOkMsg(''), 2000);
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  const handleMoveQuestion = async (sponsor, qIndex, delta) => {
    const ids = [...(sponsorQuestionIds[sponsor.id] || [])];
    const j = qIndex + delta;
    if (j < 0 || j >= ids.length) return;
    [ids[qIndex], ids[j]] = [ids[j], ids[qIndex]];
    try {
      await persistSponsorQuestions(sponsor.id, ids, sponsor);
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  const handleAddQuestionFromBank = async (sponsor, questionId) => {
    const qid = (questionId || '').trim();
    if (!qid) return;
    const ids = [...(sponsorQuestionIds[sponsor.id] || [])];
    if (ids.includes(qid)) return;
    ids.push(qid);
    try {
      await persistSponsorQuestions(sponsor.id, ids, sponsor);
      setPickSponsorModal(null);
      setOkMsg('Question added');
      setTimeout(() => setOkMsg(''), 2000);
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  const handleSaveNewQuestion = async (form, targetSponsor) => {
    if (!(form.prompt || '').trim()) return;
    try {
      const id = await createMainClueQuestion({
        prompt: form.prompt,
        answer: form.answer,
        tags: parseTags(form.tags),
        notes: form.notes,
      });
      await refreshQuestions();
      if (targetSponsor) {
        await handleAddQuestionFromBank(targetSponsor, id);
      }
      setQModal(null);
      setNewQForSponsor(null);
      setOkMsg('Question created');
      setTimeout(() => setOkMsg(''), 2000);
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  const openPrint = async (mode) => {
    setError('');
    const qMap = {};
    try {
      const list = await listMainClueQuestions();
      list.forEach((q) => {
        qMap[q.id] = q;
      });
    } catch (e) {
      setError(e.message || 'Failed to load questions for print');
      return;
    }
    const setsOnly =
      printSetScope === 'all' ? null : printSetScope === 'A' ? ['A'] : printSetScope === 'B' ? ['B'] : ['C'];
    const body = buildSponsorRunSheetsHtml({
      eventTitle: eventMeta?.title || '',
      sponsors,
      setAssignments: firestoreSetMap,
      sponsorQuestionIds,
      questionsById: qMap,
      mode,
      setsOnly,
    });
    const title =
      mode === 'answerKey'
        ? `${eventMeta?.title || 'Event'} — Sponsor Run answer key`
        : `${eventMeta?.title || 'Event'} — Sponsor Run sheets`;
    const returnUrl = `${window.location.pathname}${window.location.search}`;
    openPrintWindow(title, body, getSponsorRunPrintCss(), returnUrl);
  };

  const loadUsage = async (questionId) => {
    setUsageModal(questionId);
    try {
      await rebuildQuestionUsedBySponsors(questionId);
      await refreshQuestions();
      const rows = await listQuestionUsage(questionId);
      setUsageRows(rows);
    } catch {
      setUsageRows([]);
    }
  };

  const handleCopyToEventSubmit = async (questionId, sponsorId) => {
    const sp = sponsors.find((s) => s.id === sponsorId);
    if (!sp) return;
    await handleAddQuestionFromBank(sp, questionId);
    setCopyModal(null);
  };

  return (
    <div className="admin-dashboard main-clue-sr">
      <h1>Sponsor Run / Main clue questions</h1>
      <p className="main-clue-sr-sub">
        Question bank, per-sponsor assignments for interactive vendors, and printouts for teams and staff.
      </p>

      <div className="main-clue-sr-toolbar">
        <label>
          Event
          <select
            value={selectedEventId}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedEventId(v);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (v) next.set('event', v);
                else next.delete('event');
                return next;
              });
            }}
          >
            {events.length === 0 ? <option value="">—</option> : null}
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title || ev.id}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="main-clue-sr-msg main-clue-sr-msg--err" role="alert">
          {error}
          <button type="button" className="main-clue-sr-btn" style={{ marginLeft: 8 }} onClick={() => setError('')}>
            Dismiss
          </button>
        </div>
      ) : null}
      {okMsg ? <div className="main-clue-sr-msg main-clue-sr-msg--ok">{okMsg}</div> : null}

      <div className="main-clue-sr-tabs">
        <button
          type="button"
          className={`main-clue-sr-tab ${tab === TABS.event ? 'main-clue-sr-tab--active' : ''}`}
          onClick={() => setTab(TABS.event)}
        >
          This event
        </button>
        <button
          type="button"
          className={`main-clue-sr-tab ${tab === TABS.library ? 'main-clue-sr-tab--active' : ''}`}
          onClick={() => setTab(TABS.library)}
        >
          Question library
        </button>
        <button
          type="button"
          className={`main-clue-sr-tab ${tab === TABS.print ? 'main-clue-sr-tab--active' : ''}`}
          onClick={() => setTab(TABS.print)}
        >
          Print
        </button>
      </div>

      {tab === TABS.library && (
        <>
          <div className="main-clue-sr-toolbar">
            <input
              type="search"
              className="main-clue-sr-search"
              placeholder="Search prompts, tags, years, sets, or prior sponsors…"
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
            />
            <button
              type="button"
              className="main-clue-sr-btn btn-fm-primary"
              onClick={() => setQModal({ mode: 'create' })}
            >
              New question
            </button>
            <button type="button" className="main-clue-sr-btn" onClick={() => refreshQuestions()} disabled={questionsLoading}>
              Refresh
            </button>
          </div>
          <div className="main-clue-sr-table-wrap">
            <table className="main-clue-sr-table">
              <thead>
                <tr>
                  <th>Prompt</th>
                  <th>Tags</th>
                  <th>Prior sponsors</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLibrary.map((q) => (
                  <tr key={q.id}>
                    <td className="main-clue-sr-prompt-preview" title={q.prompt}>
                      {q.prompt || '—'}
                    </td>
                    <td>{(q.tags || []).join(', ') || '—'}</td>
                    <td className="main-clue-sr-sponsor-tags main-clue-sr-prior-use-cell" title={formatMainCluePriorUseLine(q)}>
                      {formatMainCluePriorUseShort(q)}
                    </td>
                    <td>
                      <div className="main-clue-sr-actions">
                        <button type="button" className="main-clue-sr-btn" onClick={() => setQModal({ mode: 'edit', q })}>
                          Edit
                        </button>
                        <button type="button" className="main-clue-sr-btn" onClick={() => loadUsage(q.id)}>
                          History
                        </button>
                        <button
                          type="button"
                          className="main-clue-sr-btn"
                          onClick={() => setCopyModal(q)}
                          disabled={!selectedEventId || sponsors.length === 0}
                        >
                          Copy to event
                        </button>
                        <button
                          type="button"
                          className="main-clue-sr-btn main-clue-sr-btn--danger"
                          onClick={async () => {
                            if (!window.confirm('Delete this question from the bank?')) return;
                            try {
                              await deleteMainClueQuestion(q.id);
                              await refreshQuestions();
                            } catch (e) {
                              setError(e.message || 'Delete failed');
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === TABS.event && (
        <>
          {sponsorsLoading ? (
            <p>Loading sponsors…</p>
          ) : sponsors.length === 0 ? (
            <p className="main-clue-sr-print-hint">No interactive sponsors (visible on dashboard) for this event.</p>
          ) : (
            <div className="main-clue-sr-table-wrap">
              <table className="main-clue-sr-table">
                <thead>
                  <tr>
                    <th>Sponsor</th>
                    <th>Set</th>
                    <th>Questions (order = print order)</th>
                  </tr>
                </thead>
                <tbody>
                  {sponsors.map((s) => {
                    const setVal = firestoreSetMap[s.id];
                    const current = setVal === 'A' || setVal === 'B' || setVal === 'C' ? setVal : 'none';
                    const qids = sponsorQuestionIds[s.id] || [];
                    return (
                      <tr key={s.id} className="main-clue-sr-sponsor-row">
                        <td>{s.name || s.companyName || s.id}</td>
                        <td>
                          <select
                            className="main-clue-sr-set-select"
                            value={current}
                            onChange={(e) => handleSetChange(s.id, e.target.value)}
                          >
                            <option value="none">Not in set</option>
                            <option value="A">Set A</option>
                            <option value="B">Set B</option>
                            <option value="C">Set C</option>
                          </select>
                        </td>
                        <td>
                          {qids.map((qid, idx) => {
                            const q = questionsById[qid];
                            const label = q ? q.prompt : qid;
                            return (
                              <div key={`${s.id}-${qid}-${idx}`} className="main-clue-sr-q-row">
                                <button
                                  type="button"
                                  className="main-clue-sr-btn"
                                  aria-label="Move up"
                                  disabled={idx === 0}
                                  onClick={() => handleMoveQuestion(s, idx, -1)}
                                >
                                  ↑
                                </button>
                                <button
                                  type="button"
                                  className="main-clue-sr-btn"
                                  aria-label="Move down"
                                  disabled={idx >= qids.length - 1}
                                  onClick={() => handleMoveQuestion(s, idx, 1)}
                                >
                                  ↓
                                </button>
                                <span title={label}>{label.length > 120 ? `${label.slice(0, 117)}…` : label}</span>
                                <button type="button" className="main-clue-sr-btn main-clue-sr-btn--danger" onClick={() => handleRemoveQuestion(s, idx)}>
                                  Remove
                                </button>
                              </div>
                            );
                          })}
                          <div className="main-clue-sr-actions" style={{ marginTop: 6 }}>
                            <button type="button" className="main-clue-sr-btn" onClick={() => setPickSponsorModal(s)}>
                              Add from library…
                            </button>
                            <button type="button" className="main-clue-sr-btn" onClick={() => setNewQForSponsor(s)}>
                              New question…
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === TABS.print && (
        <div>
          <p className="main-clue-sr-print-hint">
            Uses set assignments and question lists from the This event tab. Use your browser’s Print dialog and “Save as PDF” if needed.
          </p>
          <div className="main-clue-sr-print-actions">
            <label>
              Sets
              <select value={printSetScope} onChange={(e) => setPrintSetScope(e.target.value)}>
                <option value="all">All sets (A, B, C)</option>
                <option value="A">Set A only</option>
                <option value="B">Set B only</option>
                <option value="C">Set C only</option>
              </select>
            </label>
            <button type="button" className="main-clue-sr-btn btn-fm-primary" onClick={() => openPrint('participant')}>
              Sponsor Run (teams)
            </button>
            <button type="button" className="main-clue-sr-btn btn-fm-primary" onClick={() => openPrint('answerKey')}>
              Answer key (staff)
            </button>
          </div>
        </div>
      )}

      {qModal && (
        <QuestionEditModal
          mode={qModal.mode}
          initial={qModal.q}
          onClose={() => setQModal(null)}
          onSave={async (form) => {
            if (!(form.prompt || '').trim()) return;
            try {
              if (qModal.mode === 'create') {
                await createMainClueQuestion({
                  prompt: form.prompt,
                  answer: form.answer,
                  tags: parseTags(form.tags),
                  notes: form.notes,
                });
              } else if (qModal.q) {
                await updateMainClueQuestion(qModal.q.id, {
                  prompt: form.prompt,
                  answer: form.answer,
                  tags: parseTags(form.tags),
                  notes: form.notes,
                });
              }
              await refreshQuestions();
              setQModal(null);
              setOkMsg('Saved');
              setTimeout(() => setOkMsg(''), 2000);
            } catch (e) {
              setError(e.message || 'Save failed');
            }
          }}
        />
      )}

      {usageModal && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setUsageModal(null)}>
          <div
            className="fm-modal fm-modal--md main-clue-sr-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="main-clue-usage-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="main-clue-usage-title">
                Usage history
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setUsageModal(null)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
            <ul className="main-clue-sr-usage-list">
              {usageRows.length === 0 ? (
                <li>No recorded usage yet.</li>
              ) : (
                usageRows.map((u) => (
                  <li key={u.id}>
                    {u.sponsorNameSnapshot || u.sponsorParticipationId} — Set {u.setId || '?'} — event {u.eventId}
                    {u.eventYear != null ? ` (${u.eventYear})` : ''}
                  </li>
                ))
              )}
            </ul>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-primary" onClick={() => setUsageModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {copyModal && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setCopyModal(null)}>
          <div
            className="fm-modal fm-modal--md main-clue-sr-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="main-clue-copy-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="main-clue-copy-title">
                Copy to sponsor
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setCopyModal(null)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
              <p className="main-clue-sr-print-hint">
                Appends this question to the sponsor’s list for the selected event.
              </p>
              <div className="main-clue-sr-field">
                <label htmlFor="copy-sponsor">Sponsor</label>
                <select
                  id="copy-sponsor"
                  value={copySponsorId}
                  onChange={(e) => setCopySponsorId(e.target.value)}
                >
                  {sponsors.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name || s.id}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setCopyModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-primary"
                onClick={() => handleCopyToEventSubmit(copyModal.id, copySponsorId)}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {pickSponsorModal && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setPickSponsorModal(null)}>
          <div
            className="fm-modal fm-modal--md main-clue-sr-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="main-clue-pick-q-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="main-clue-pick-q-title">
                Add question for {pickSponsorModal.name || pickSponsorModal.id}
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setPickSponsorModal(null)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
              <PickQuestionFromLibrary
                key={pickSponsorModal.id}
                questions={questions}
                forSponsor={pickSponsorModal}
                excludeIds={new Set(sponsorQuestionIds[pickSponsorModal.id] || [])}
                onPick={(qid) => handleAddQuestionFromBank(pickSponsorModal, qid)}
              />
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setPickSponsorModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {newQForSponsor && (
        <QuestionEditModal
          mode="create"
          initial={null}
          titleOverride="New question (add to bank and this sponsor)"
          onClose={() => setNewQForSponsor(null)}
          onSave={(form) => handleSaveNewQuestion(form, newQForSponsor)}
        />
      )}
    </div>
  );
}

function PickQuestionFromLibrary({ questions, forSponsor, excludeIds, onPick }) {
  const [term, setTerm] = useState('');
  const { rows, emptyHint } = useMemo(() => {
    const t = term.trim().toLowerCase();
    const base = questions.filter((q) => !excludeIds.has(q.id));
    const matchesFilter = (q) => {
      if (!t) return false;
      const prompt = (q.prompt || '').toLowerCase();
      const tagHay = (q.tags || []).join(' ').toLowerCase();
      const usageHay = mainClueQuestionUsageHaystack(q);
      return prompt.includes(t) || tagHay.includes(t) || usageHay.includes(t);
    };
    const sorted = [...base].sort((a, b) => {
      if (t) {
        const matchA = matchesFilter(a);
        const matchB = matchesFilter(b);
        if (matchA !== matchB) return matchA ? -1 : 1;
      }
      const usedA = mainClueQuestionUsedByPickingSponsor(a, forSponsor);
      const usedB = mainClueQuestionUsedByPickingSponsor(b, forSponsor);
      if (usedA !== usedB) return usedA ? -1 : 1;
      return (a.prompt || '').localeCompare(b.prompt || '', undefined, { sensitivity: 'base' });
    });
    let hint = '';
    if (sorted.length === 0) {
      hint = 'No questions left to add (all are already on this sponsor’s list).';
    } else if (t && !sorted.some(matchesFilter)) {
      hint = 'No questions match this filter — try another year, sponsor name, or keyword.';
    }
    return { rows: sorted, emptyHint: hint };
  }, [questions, excludeIds, term, forSponsor]);

  return (
    <>
      <p className="main-clue-sr-pick-hint">
        Prior use by sponsor and year (when on file). Filter matches sort to the top; questions this vendor used before sort above others when the filter is empty.
      </p>
      <input
        type="search"
        className="main-clue-sr-search"
        placeholder="Filter prompt, tags, sponsor, year, set A/B/C…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
      />
      <div className="main-clue-sr-pick-list">
        {rows.map((q) => (
          <div key={q.id} className="main-clue-sr-pick-row">
            <div className="main-clue-sr-pick-row-actions">
              <button type="button" className="fm-modal-btn fm-modal-btn-primary main-clue-sr-pick-add" onClick={() => onPick(q.id)}>
                Add
              </button>
            </div>
            <div className="main-clue-sr-pick-row-body">
              <div className="main-clue-sr-pick-prompt">{q.prompt}</div>
              <div className="main-clue-sr-pick-prior" title={formatMainCluePriorUseLine(q)}>
                {formatMainCluePriorUseLine(q)}
              </div>
            </div>
          </div>
        ))}
        {emptyHint ? <p className="main-clue-sr-pick-empty">{emptyHint}</p> : null}
      </div>
    </>
  );
}

function QuestionEditModal({ mode, initial, titleOverride, onClose, onSave }) {
  const [prompt, setPrompt] = useState(initial?.prompt || '');
  const [answer, setAnswer] = useState(initial?.answer || '');
  const [tags, setTags] = useState(tagsToString(initial?.tags));
  const [notes, setNotes] = useState(initial?.notes || '');

  useEffect(() => {
    setPrompt(initial?.prompt || '');
    setAnswer(initial?.answer || '');
    setTags(tagsToString(initial?.tags));
    setNotes(initial?.notes || '');
  }, [initial, mode]);

  useModalEscapeKey(true, onClose);

  return (
    <div className="fm-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="fm-modal fm-modal--md main-clue-sr-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="main-clue-qedit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fm-modal-header">
          <h2 className="fm-modal-title" id="main-clue-qedit-title">
            {titleOverride || (mode === 'create' ? 'New question' : 'Edit question')}
          </h2>
          <button type="button" className="fm-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="fm-modal-body">
        <div className="main-clue-sr-field">
          <label htmlFor="mq-prompt">Prompt</label>
          <textarea id="mq-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        </div>
        <div className="main-clue-sr-field">
          <label htmlFor="mq-answer">Answer (staff only)</label>
          <textarea id="mq-answer" value={answer} onChange={(e) => setAnswer(e.target.value)} />
        </div>
        <div className="main-clue-sr-field">
          <label htmlFor="mq-tags">Tags (comma-separated)</label>
          <input id="mq-tags" value={tags} onChange={(e) => setTags(e.target.value)} />
        </div>
        <div className="main-clue-sr-field">
          <label htmlFor="mq-notes">Internal notes</label>
          <textarea id="mq-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
        </div>
        </div>
        <div className="fm-modal-footer">
          <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="fm-modal-btn fm-modal-btn-primary"
            onClick={() => {
              if (!(prompt || '').trim()) return;
              onSave({ prompt, answer, tags, notes });
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
